import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, of } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';

interface UnidadFuncional {
  id_unidad_funcional: number;
  nombre: string;
}

interface Area {
  id_area: number;
  nombre: string;
}

@Component({
  selector: 'app-especialista-documentacion',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './especialista-documentacion.component.html',
  styleUrl: './especialista-documentacion.component.css',
})
export class EspecialistaDocumentacionComponent implements OnInit {
  especialistaForm!: FormGroup;
  id_rol!: number;
  id_rol_usuario!: number;
  id_usuario!: number;
  showPassword = false;
  claveOriginal: string = '';
  showUnidadFuncional: boolean = false; // Controla la visibilidad de Unidad Funcional
  showArea: boolean = false; // Controla la visibilidad de Área
  unidadesFuncionales: UnidadFuncional[] = [];
  areas: Area[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.especialistaForm = this.fb.group({
      nombres: [
        { value: ''},
        [Validators.required, Validators.maxLength(100)],
      ],
      apellidos: [
        { value: ''},
        [Validators.required, Validators.maxLength(100)],
      ],
      correo: [
        { value: ''},
        [Validators.required, Validators.email],
      ],
      codigo_documento: [
        { value: '' },
        [Validators.required, Validators.pattern(/^\d{8}$/)],
      ],
      clave: [{ value: ''}],
      id_unidad_funcional: [{ value: null }], // Campo para Unidad Funcional
      id_area: [{ value: null}], // Campo para Área
    });

    this.route.queryParams.subscribe((params) => {
      const id_rol_usuario = parseInt(params['id_rol_usuario'], 10);
      const id_rol = parseInt(params['id_rol'], 10);

      if (!isNaN(id_rol_usuario) && !isNaN(id_rol)) {
        this.id_rol_usuario = id_rol_usuario;
        this.id_rol = id_rol;
        this.updateFormFields(); // Actualizar campos según el rol
      } else {
        Swal.fire('Error', 'IDS de usuario no válido.', 'error');
        this.router.navigate(['/especialistas_documentacion']);
      }
    });

    forkJoin([
      this.http.get<UnidadFuncional[]>(`${environment.apiUrl}/unidad-funcional`),
      this.http.get<Area[]>(`${environment.apiUrl}/area`)
    ]).subscribe(([unidades, areas]) => {
      this.unidadesFuncionales = unidades;
      this.areas = areas;
    });

    this.cargarEspecialista();
  }

  updateFormFields(): void {
    if (this.id_rol === 2) {
      this.showUnidadFuncional = true;
      this.showArea = false;
      this.especialistaForm.get('id_unidad_funcional')?.setValidators([Validators.required]);
      this.especialistaForm.get('id_area')?.clearValidators();
      this.especialistaForm.get('id_area')?.setValue(null);
    } else if (this.id_rol === 3) {
      this.showUnidadFuncional = false;
      this.showArea = true;
      this.especialistaForm.get('id_area')?.setValidators([Validators.required]);
      this.especialistaForm.get('id_unidad_funcional')?.clearValidators();
      this.especialistaForm.get('id_unidad_funcional')?.setValue(null);
    }
    this.especialistaForm.get('id_unidad_funcional')?.updateValueAndValidity();
    this.especialistaForm.get('id_area')?.updateValueAndValidity();
  }

  cargarEspecialista(): void {
    const url = environment.apiUrl + `/rol-usuario/${this.id_rol_usuario}`;
    this.http
      .get(url)
      .pipe(
        catchError((error) => {
          Swal.fire('Error',  error.error.message || 'No se pudo obtener el usuario.', 'error');
          return of(null);
        })
      )
      .subscribe((response: any) => {
        if (response && response.usuario) {
          this.claveOriginal = response.usuario.clave;
          this.especialistaForm.patchValue({
            nombres: response.usuario.nombres || '',
            apellidos: response.usuario.apellidos || '',
            correo: response.usuario.correo || '',
            codigo_documento: response.usuario.codigo_documento || '',
            clave: '', // El campo de clave aparece vacío
          });

          if (this.id_rol === 2 && response.unidadFuncional) {
            const unidad = this.unidadesFuncionales.find(
              uf => uf.id_unidad_funcional === response.unidadFuncional?.id_unidad_funcional
            );
            this.especialistaForm.patchValue({
              id_unidad_funcional: response.unidadFuncional?.id_unidad_funcional || null,
            });
          } else if (this.id_rol === 3) {
            const area = this.areas.find(
              a => a.id_area === response.area?.id_area
            );
            this.especialistaForm.patchValue({
              id_area: response.area?.id_area || null,
            });
          }

          this.id_usuario = response.usuario.id_usuario;
        } else {
          Swal.fire('Error', 'Datos del especialista no disponibles.', 'error');
        }
      },
      (error) => {
        Swal.fire('Error', error.error.message || 'No se pudo obtener el usuario.', 'error');
      }
    );
  }

  actualizarEspecialista(): void {
    if (this.especialistaForm.invalid) {
      Swal.fire('Advertencia', 'Hay errores en el formulario.', 'warning');
      return;
    }

    this.especialistaForm.enable();

    const rolUsuarioData: any = {};
    if (this.id_rol === 2) {
      rolUsuarioData.id_unidad_funcional = parseInt(
        this.especialistaForm.value.id_unidad_funcional
      );
    } else if (this.id_rol === 3) {
      rolUsuarioData.id_area = parseInt(this.especialistaForm.value.id_area);
    }

    const nuevaClave = this.especialistaForm.value.clave;
    const usuarioData: any = {
      nombres: this.especialistaForm.value.nombres,
      apellidos: this.especialistaForm.value.apellidos,
      correo: this.especialistaForm.value.correo,
      codigo_documento: this.especialistaForm.value.codigo_documento,
    };

    if (nuevaClave) {
      usuarioData.clave = nuevaClave;
    }

    Swal.fire({
      title: '¿Estás seguro?',
      text: '¿Quieres actualizar el especialista?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'No, cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        forkJoin({
          rolUsuario: this.http.patch(
            environment.apiUrl + `/rol-usuario/${this.id_rol_usuario}`,
            rolUsuarioData
          ),
          usuario: this.http.patch(
            environment.apiUrl + `/usuario/${this.id_usuario}`,
            usuarioData
          ),
        })
          .subscribe((response) => {
            if (response) {
              Swal.fire('Éxito', 'Especialista actualizado correctamente.', 'success').then(() => {
                this.router.navigate(['/especialistas-documentacion']);
              });
            }
          },
          (error) => {
            //console.error(error);
            Swal.fire('Error', error.error.message || 'Error al actualizar el especialista.', 'error');
          });
      }
    });


  }

  volver(): void {
    if (this.especialistaForm.dirty ) {
      Swal.fire({
        title: 'Cambios no guardados',
        text: '¿Estás seguro de salir sin guardar los cambios?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, salir',
        cancelButtonText: 'No, quedarse',
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/especialistas-documentacion']);
        }
      });
    } else {
      this.router.navigate(['/especialistas-documentacion']);
    }
    this.especialistaForm.disable();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}
